import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import {
  useParams,
  useNavigate,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  openModal,
  closeModal,
  stockSummaryItemVoucherList,
  voucherEntryDelete,
} from "../../../../redux/actions/action-creator";
import TableLayoutItem from "../../../../components/Table/TableLayoutItem";
import NavLayout from "../../../../components/NavNameOfComponent/NavLayout";
import Period from "../../../../components/help/stock_summary/stock_summary_component/Period";
import Configure from "../../../../components/help/stock_summary/stock_summary_component/Configure";
import ChangeView from "../../../../components/help/stock_summary/stockitemsummary/ChangeView";
import GodownFilter from "../../../../components/help/stock_summary/stockitemsummary/GodownFilter";
import StockItemVoucherFooter from "../../../../components/Footer/StockItemVoucherFooter";
import StockItemVoucherHelp from "../../../../components/help/stock_summary/StockItemVoucher/StockItemVoucherHelp";
import StockItemFilter from "../../../../components/help/stock_summary/StockItemVoucher/StockItemFilter";
import {
  currencyFormate,
  dateFormateShow,
  dateFormateSend,
} from "../../../../components/Date_Formate_Utilitiy/Index";
import "../../../../components/Table/tablelayoutItem.css";
import { useDropdownScroll } from "../../../../components/Custom_Hooks/useDropdownScroll";
import { useDropdownKeyboardNavigation } from "../../../../components/Custom_Hooks/useDropdownKeyboardNavigation";
import Indicator from "../../../../components/Indicator/Indicator";
import DeletConfirm from "../../../../components/Modal/DeletConfirm";
import usePageState from "../../../../components/Custom_Hooks/usePageState";

const parseNumeric = (val) => {
  if (val === null || val === undefined) return 0;
  if (typeof val === "number") return val;
  const cleanVal = String(val).replace(/,/g, "");
  const match = cleanVal.match(/[-+]?[0-9]*\.?[0-9]+/);
  return match ? parseFloat(match[0]) : 0;
};

const normalizeVoucherType = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim().toLowerCase();
};

const isDebitNote = (item) => {
  const vouchers = [
    item?.voucher_type,
    item?.voucher_type_name,
    item?.inwardData?.voucher_type,
    item?.inwardData?.voucher_type_name,
    item?.outwardData?.voucher_type,
    item?.outwardData?.voucher_type_name,
  ];
  return vouchers.some((value) => normalizeVoucherType(value) === "debit note");
};

const isCreditNote = (item) => {
  const vouchers = [
    item?.voucher_type,
    item?.voucher_type_name,
    item?.inwardData?.voucher_type,
    item?.inwardData?.voucher_type_name,
    item?.outwardData?.voucher_type,
    item?.outwardData?.voucher_type_name,
  ];
  return vouchers.some(
    (value) => normalizeVoucherType(value) === "credit note",
  );
};

const getEffectiveVoucherFields = (item) => {
  const originalInwardQty = parseNumeric(item?.inwardData?.quantity);
  const originalInwardValue = parseNumeric(item?.inwardData?.value);
  const originalInwardRate = parseNumeric(item?.inwardData?.rate);
  const originalOutwardQty = parseNumeric(item?.outwardData?.quantity);
  const originalOutwardValue = parseNumeric(item?.outwardData?.value);
  const originalOutwardRate = parseNumeric(item?.outwardData?.rate);

  let inwardQty = originalInwardQty;
  let inwardValue = originalInwardValue;
  let inwardRate = originalInwardRate;
  let inwardQtyText = item?.inwardData?.quantity || "";
  let inwardValueText = item?.inwardData?.value || "";
  let outwardQty = originalOutwardQty;
  let outwardValue = originalOutwardValue;
  let outwardRate = originalOutwardRate;
  let outwardQtyText = item?.outwardData?.quantity || "";
  let outwardValueText = item?.outwardData?.value || "";

  if (isCreditNote(item) && outwardQty === 0 && originalInwardQty > 0) {
    outwardQty = originalInwardQty;
    outwardValue = originalInwardValue;
    outwardRate = originalOutwardRate || originalInwardRate;
    outwardQtyText = item?.inwardData?.quantity || outwardQtyText;
    outwardValueText = item?.inwardData?.value || outwardValueText;
    inwardQty = 0;
    inwardValue = 0;
    inwardRate = 0;
    inwardQtyText = "";
    inwardValueText = "";
  }

  if (isDebitNote(item) && inwardQty === 0 && originalOutwardQty > 0) {
    inwardQty = originalOutwardQty;
    inwardValue = originalOutwardValue;
    inwardRate = originalInwardRate || originalOutwardRate;
    inwardQtyText = item?.outwardData?.quantity || inwardQtyText;
    inwardValueText = item?.outwardData?.value || inwardValueText;
    outwardQty = 0;
    outwardValue = 0;
    outwardRate = 0;
    outwardQtyText = "";
    outwardValueText = "";
  }

  return {
    inwardQty,
    inwardValue,
    inwardRate,
    inwardQtyText,
    inwardValueText,
    outwardQty,
    outwardValue,
    outwardRate,
    outwardQtyText,
    outwardValueText,
  };
};

const getEffectiveVoucherAmounts = (item) => {
  const originalInwardQty = parseNumeric(item?.inwardData?.quantity);
  const originalInwardValue = parseNumeric(item?.inwardData?.value);
  const originalOutwardQty = parseNumeric(item?.outwardData?.quantity);
  const originalOutwardValue = parseNumeric(item?.outwardData?.value);

  let inwardQty = originalInwardQty;
  let inwardValue = originalInwardValue;
  let outwardQty = originalOutwardQty;
  let outwardValue = originalOutwardValue;

  if (isCreditNote(item) && outwardQty === 0 && originalInwardQty > 0) {
    outwardQty = originalInwardQty;
    outwardValue = originalInwardValue;
    inwardQty = 0;
    inwardValue = 0;
  }

  if (isDebitNote(item) && inwardQty === 0 && originalOutwardQty > 0) {
    inwardQty = originalOutwardQty;
    inwardValue = originalOutwardValue;
    outwardQty = 0;
    outwardValue = 0;
  }

  return { inwardQty, inwardValue, outwardQty, outwardValue };
};

const StockItemVoucher = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const navType = useNavigationType();
  const isBackOrReload = navType === "POP";

  const boxRef = useRef(null);

  const { company_session } = useSelector((state) => state.common);
  const { userData } = useSelector((state) => state.auth);
  const modal_state = useSelector((state) => state.common.modal_state);

  const [loading, setLoading] = useState(false);
  const [state_configure, setState_configure] = useState({
    show_quantity: "No",
    show_alternate_unit: "No",
    show_tail_unit_of_compound_unit: "No",
    show_rate: "No",
    show_value: "No",
    show_opening_balance: "No",
    show_goods_inwards: "No",
    show_goods_outwards: "No",
    show_gross_value_of_outwards: "No",
    show_comsumption_and_gross_profit: "No",
    show_gross_profits_on_gross_value: "No",
    show_closing_balance: "No",
    show_stock_item_with_zero_qty_or_blc: "No",
    exclude_detail_with_no_transaction: "No",
    type_of_grouping: "",
    items_formate_of_report: "Condensed",
    godowns_formate_of_report: "Hide",
    expand_all_level_in_details_formate: "No",
    display_name: "Name Only",
    sorting_method: "Default",
  });
  const [godowns_formate_of_report, setGodowns_formate_of_report] =
    useState("Hide");
  const [selectedItems, setSelectedItems] = useState([]);
  const [removedItems, setRemovedItems] = useState([]);
  const [data, setData] = useState([]);
  const [displayData, setDisplayData] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  useEffect(() => {
    if (modal_state?.detailedCondensed) {
      setGodowns_formate_of_report((prev) =>
        prev === "Hide" ? "Show" : "Hide",
      );
      dispatch(closeModal("detailedCondensed"));
    }
  }, [modal_state?.detailedCondensed, dispatch]);

  const initialDate = useMemo(
    () => ({
      send_from_date:
        location.state?.send_from_date ||
        dateFormateSend(company_session?.start_financial_year),
      send_to_date:
        location.state?.send_to_date ||
        dateFormateSend(company_session?.current_date),
      location_id: location.state?.location_id || "",
      location_name: location.state?.location_name || "",
      filter_name: location.state?.filter_name || "Item-Wise",
      filter_value_names: location.state?.filter_value_names || "",
      stock_group_id: location.state?.stock_group_id || "",
      stock_category_id: location.state?.stock_category_id || "",
      stock_company_id: location.state?.stock_company_id || "",
    }),
    [company_session, location.state],
  );

  const [date, setDate] = usePageState(
    `stock_item_voucher_date_${id}`,
    initialDate,
    { ignoreSavedState: !isBackOrReload },
  );
  const [savedIndex, setSavedIndex] = usePageState(
    `stock_item_voucher_index_${id}`,
    0,
    { ignoreSavedState: !isBackOrReload },
  );

  // Memoized function to avoid unnecessary re-renders
  const fetchData = useCallback(async () => {
    setLoading(true);
    const formdata = new FormData();
    formdata.append("company_id", userData?.company_id);
    formdata.append("fromDate", dateFormateSend(date?.send_from_date));
    formdata.append("toDate", dateFormateSend(date?.send_to_date));
    formdata.append("location_id", date?.location_id);
    formdata.append("item_id", id);
    formdata.append("stock_gp_id", date?.stock_group_id);
    formdata.append("stock_cat_id", date?.stock_category_id);
    formdata.append("stock_company_id", date?.stock_company_id);

    try {
      const response = await dispatch(stockSummaryItemVoucherList(formdata));
      console.log(response, "response---------------");
      if (response.status) {
        toast.success(response?.message || "Stock Summary Item !", {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
        setData(response.data);
      } else {
        toast.warn(response?.message || "Stock Summary Item !", {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    } catch (error) {
      toast.error("Failed to fetch Stock Summary Item!", {
        position: toast.POSITION.TOP_CENTER,
      });
    } finally {
      setLoading(false);
    }
  }, [date, id, dispatch, userData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  let qtyNum =
    (data?.opening_data?.quantity
      ? String(data.opening_data.quantity).match(/\d+/g)
      : null) || 0;
  let qtyUnitName =
    (data?.opening_data?.quantity
      ? String(data.opening_data.quantity).match(/[a-z]+/gi)
      : null) || 0;

  let unitName = "";
  if (Array.isArray(qtyUnitName) && qtyUnitName.length > 0) {
    unitName = qtyUnitName[0];
  } else if (data?.voucher_item_data?.length > 0) {
    for (let item of data.voucher_item_data) {
      let match =
        (item?.inwardData?.quantity
          ? String(item.inwardData.quantity).match(/[a-zA-Z]+/g)
          : null) ||
        (item?.outwardData?.quantity
          ? String(item.outwardData.quantity).match(/[a-zA-Z]+/g)
          : null);
      if (match) {
        unitName = match[0];
        break;
      }
    }
  }

  useEffect(() => {
    if (!data?.voucher_item_data) {
      setDisplayData([]);
      return;
    }

    let baseData = data.voucher_item_data;

    // Group items by voucher (using voucher_entries_id, uid, or id)
    const groupedByVoucher = {};
    const order = [];
    baseData.forEach((item) => {
      const vId = item.voucher_entries_id || item.uid || item.id;
      if (!groupedByVoucher[vId]) {
        groupedByVoucher[vId] = {
          ...item,
          inwardQtySum: 0,
          inwardValueSum: 0,
          outwardQtySum: 0,
          outwardValueSum: 0,
        };
        order.push(vId);
      }

      const target = groupedByVoucher[vId];

      let item_iw_qty = parseNumeric(item?.inwardData?.quantity);
      let item_iw_value = parseNumeric(item?.inwardData?.value);

      let item_Ow_qty = parseNumeric(item?.outwardData?.quantity);
      let item_Ow_value = parseNumeric(item?.outwardData?.value);

      target.inwardQtySum += item_iw_qty;
      target.inwardValueSum += item_iw_value;
      target.outwardQtySum += item_Ow_qty;
      target.outwardValueSum += item_Ow_value;
    });

    const finalVouchers = order.map((vId) => {
      const v = groupedByVoucher[vId];
      const inwardRate =
        v.inwardQtySum > 0 ? v.inwardValueSum / v.inwardQtySum : 0;
      const outwardRate =
        v.outwardQtySum > 0 ? v.outwardValueSum / v.outwardQtySum : 0;

      return {
        ...v,
        inwardData: {
          ...v.inwardData,
          quantity:
            v.inwardQtySum > 0 ? `${v.inwardQtySum} ${unitName}`.trim() : null,
          rate: inwardRate,
          value: v.inwardValueSum,
        },
        outwardData: {
          ...v.outwardData,
          quantity:
            v.outwardQtySum > 0
              ? `${v.outwardQtySum} ${unitName}`.trim()
              : null,
          rate: outwardRate,
          value: v.outwardValueSum,
        },
      };
    });

    setDisplayData(finalVouchers);
  }, [data?.voucher_item_data, unitName]);

  const openingQty = parseNumeric(data?.opening_data?.quantity);
  const openingValue = parseNumeric(data?.opening_data?.value);

  let cumulativeQty = openingQty;
  let cumulativeValue = openingValue;
  let cumulativeRate = cumulativeQty > 0 ? cumulativeValue / cumulativeQty : 0;
  let lastKnownRate = cumulativeRate;

  const { selectedItemIndex, setSelectedItemIndex } =
    useDropdownKeyboardNavigation({
      items: displayData,
      initialIndex: savedIndex,
      state: Object.keys(modal_state || {}).some((key) => modal_state[key])
        ? false
        : true,
      onItemSelect: (item) => {
        if (!item?.is_godown_row && !item?.is_location_header) {
          navigate(
            `../voucher-transaction-view/${item?.voucher_entries_id > 0 ? item?.voucher_entries_id : item?.uid}`,
          );
        }
      },
      onItemControlEnterSelect: (item) => {
        if (!item?.is_godown_row && !item?.is_location_header) {
          navigate(
            `../voucher-transaction-alter/${item?.voucher_entries_id > 0 ? item?.voucher_entries_id : item?.uid}`,
          );
        }
      },
      onItemAltASelect: (item) => {
        if (!item?.is_godown_row && !item?.is_location_header) {
          navigate(`../voucherstransaction`, {
            state: {
              create_date: dateFormateShow(item?.create_date),
              voucher_type_id: item?.voucher_type_id,
              voucher_type_name: item?.voucher_type_name,
            },
          });
        }
      },
      onItemAlt2Select: (item) => {
        if (!item?.is_godown_row && !item?.is_location_header) {
          navigate(
            `../voucher-transaction/${item?.voucher_entries_id > 0 ? item?.voucher_entries_id : item?.uid}`,
          );
        }
      },
      onItemAltDSelect: (item) => {
        if (!item?.is_godown_row && !item?.is_location_header) {
          setIsDeleteModalOpen(true);
        }
      },
      onItemSpaceSelect: (item) => {
        toggleSelection(selectedItemIndex);
      },
      onItemAltRSelect: (item) => {
        removeSelectedItems();
      },
      onItemAltUSelect: (item) => {
        restoreSelectedItems();
      },
      onItemF2Select: (item) => {
        dispatch(openModal("period"));
      },
      onItemF4Select: (item) => {
        dispatch(openModal("stockItem"));
      },
      onItemF5Select: (item) => {
        // Show Profit
      },
      onItemF7Select: (item) => {
        dispatch(openModal("godownWise"));
      },
      onItemF12Select: (item) => {
        dispatch(openModal("configure"));
      },
      onItemHSelect: (item) => {
        dispatch(openModal("changeView"));
      },
      onItemJSelect: (item) => {
        // Exception Reports
      },
      onItemFSelect: (item) => {
        dispatch(openModal("applyFilter"));
      },
      onItemESelect: (item) => {
        dispatch(openModal("export"));
      },
      onItemSSelect: (item) => {
        dispatch(openModal("share"));
      },
      onItemPSelect: (item) => {
        dispatch(openModal("print"));
      },
    });

  useEffect(() => {
    setSavedIndex(selectedItemIndex);
  }, [selectedItemIndex, setSavedIndex]);

  useDropdownScroll({ dropdownRef: boxRef, selectedItemIndex });

  // Function to toggle selection of an item
  const toggleSelection = (index) => {
    const selectedIndex = selectedItems.indexOf(index);
    let newSelected = [];
    if (selectedIndex === -1) {
      // Add the item to the selection if not already selected
      newSelected = newSelected.concat(selectedItems, index);
    } else if (selectedIndex === 0) {
      // Remove the item from the selection if already selected
      newSelected = newSelected.concat(selectedItems.slice(1));
    } else if (selectedIndex === selectedItems.length - 1) {
      newSelected = newSelected.concat(selectedItems.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedItems.slice(0, selectedIndex),
        selectedItems.slice(selectedIndex + 1),
      );
    }
    setSelectedItems(newSelected);
  };

  // Function to remove selected items
  const removeSelectedItems = () => {
    // If multiple items are selected, remove all selected items
    if (selectedItems.length > 0) {
      const selectedIndices = [...selectedItems].sort((a, b) => a - b); // Sort in ascending order
      const removedItemsList = [];

      // Collect all items to be removed
      selectedIndices.forEach((index) => {
        removedItemsList.push({
          index: index,
          item: displayData[index],
        });
      });

      // Filter out selected items from the list
      const updatedData = displayData.filter((_, index) => {
        return !selectedIndices.includes(index);
      });
      setDisplayData(updatedData);
      setRemovedItems([...removedItems, ...removedItemsList]);
      setSelectedItems([]); // Clear selection after removal
    } else {
      // If no multi-selection, remove single selected item
      const updatedData = displayData.filter((_, index) => {
        return index !== selectedItemIndex;
      });

      const removedItem = displayData[selectedItemIndex];

      setDisplayData(updatedData);
      setRemovedItems([
        ...removedItems,
        { index: selectedItemIndex, item: removedItem },
      ]);
      setSelectedItemIndex((prevIndex) =>
        prevIndex === updatedData.length ? prevIndex - 1 : prevIndex,
      );
    }
  };

  // Function to restore removed items
  const restoreSelectedItems = () => {
    if (removedItems.length === 0) {
      return; // No items to restore
    }

    // Restore the last removed item (LIFO - Last In, First Out)
    const lastRemovedItem = removedItems[removedItems.length - 1];
    const { index: originalIndex, item } = lastRemovedItem;

    // Find the correct position to insert the item back
    // We need to count how many items were removed before this one
    let insertIndex = originalIndex;
    const itemsRemovedBefore = removedItems.filter(
      (removedItem) => removedItem.index < originalIndex,
    ).length;

    // Adjust the insert index based on how many items were removed before this one
    insertIndex = originalIndex - itemsRemovedBefore;

    // Create updated list with the restored item at the correct position in displayData
    const updatedData = [
      ...displayData.slice(0, insertIndex),
      item,
      ...displayData.slice(insertIndex),
    ];

    setDisplayData(updatedData);

    // Set the restored item as the selected item to show visual indication
    setSelectedItemIndex(insertIndex);

    // Remove the restored item from removedItems array
    setRemovedItems(removedItems.slice(0, -1));
  };

  const handleZeroValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return "";
    }
    let zeroValue = parseFloat(value);
    if (isNaN(zeroValue) || zeroValue === 0) {
      return "";
    }
    return currencyFormate(zeroValue);
  };

  // Calculate totals using useMemo to ensure proper calculation
  const totals = useMemo(() => {
    let total_inwards_quantity = 0;
    let total_outwards_quantity = 0;
    let total_closing_quantity = 0;
    let total_inwards_value = 0;
    let total_outwards_value = 0;
    let total_closing_value = 0;

    if (data?.voucher_item_data) {
      // Initialize with opening balance
      const openingQty = parseNumeric(data?.opening_data?.quantity);
      const openingValue = parseNumeric(data?.opening_data?.value);

      let cumulativeQty = openingQty;
      let cumulativeValue = openingValue;
      let cumulativeRate =
        cumulativeQty > 0 ? cumulativeValue / cumulativeQty : 0;
      let lastKnownRate = cumulativeRate;

      data.voucher_item_data.forEach((item) => {
        const { inwardQty, inwardValue, inwardRate, outwardQty, outwardValue } =
          getEffectiveVoucherFields(item);
        let inwardRateValue = inwardRate;

        if (inwardValue === 0 && inwardQty > 0 && inwardRateValue > 0) {
          inwardValue = inwardQty * inwardRateValue;
        }

        let is_debit_note =
          item?.voucher_type === "Debit Note" ||
          item?.voucher_type_name === "Debit Note" ||
          item?.inwardData?.voucher_type === "Debit Note";
        let is_credit_note =
          item?.voucher_type === "Credit Note" ||
          item?.voucher_type_name === "Credit Note" ||
          item?.outwardData?.voucher_type === "Credit Note";

        if (is_credit_note) {
          total_outwards_quantity -= outwardQty;
          total_outwards_value -=
            outwardValue > 0 ? outwardValue : outwardQty * cumulativeRate;

          cumulativeQty += outwardQty;
          cumulativeValue +=
            outwardValue > 0 ? outwardValue : outwardQty * cumulativeRate;
        } else if (is_debit_note) {
          total_inwards_quantity -= inwardQty;
          total_inwards_value -=
            inwardValue > 0 ? inwardValue : inwardQty * cumulativeRate;

          cumulativeQty -= inwardQty;
          cumulativeValue -=
            inwardValue > 0 ? inwardValue : inwardQty * cumulativeRate;
        } else {
          total_inwards_quantity += inwardQty;
          total_inwards_value += inwardValue;

          total_outwards_quantity += outwardQty;
          total_outwards_value += outwardValue;

          if (inwardQty > 0) {
            cumulativeQty += inwardQty;
            cumulativeValue += inwardValue;
          }
          if (outwardQty > 0) {
            cumulativeQty -= outwardQty;
            cumulativeValue -= outwardQty * cumulativeRate;
          }
        }

        if (cumulativeQty > 0) {
          cumulativeRate = cumulativeValue / cumulativeQty;
          lastKnownRate = cumulativeRate;
        } else {
          cumulativeRate = lastKnownRate;
        }
      });

      total_closing_quantity = cumulativeQty;
      total_closing_value = cumulativeValue;
    }

    return {
      total_inwards_quantity,
      total_outwards_quantity,
      total_closing_quantity,
      total_inwards_value,
      total_outwards_value,
      total_closing_value,
    };
  }, [data]);

  const yPressToDelete = () => {
    setLoading(true);
    const promises = [];
    const deleteMessages = { success: [], failure: [] };

    if (selectedItems.length > 0) {
      const uidsArray = selectedItems.map((selectedIndex) => {
        const item = displayData[selectedIndex];
        return item?.uid && item?.uid !== 0 && item?.uid !== "0"
          ? item.uid
          : item?.voucher_entries_id &&
            item?.voucher_entries_id !== 0 &&
            item?.voucher_entries_id !== "0"
            ? item.voucher_entries_id
            : item?.id;
      });
      const uidsString = uidsArray.filter((id) => id).join(",");
      if (!uidsString) {
        toast.error("No valid voucher ID found for deletion");
        setLoading(false);
        return;
      }
      const formData = new FormData();
      formData.append("uid", uidsString);
      formData.append("id", uidsString);
      formData.append("company_id", userData?.company_id);

      const deletePromise = dispatch(voucherEntryDelete(formData));
      promises.push(deletePromise);
    } else {
      const item = displayData[selectedItemIndex];
      const deleteId =
        item?.uid && item?.uid !== 0 && item?.uid !== "0"
          ? item.uid
          : item?.voucher_entries_id &&
            item?.voucher_entries_id !== 0 &&
            item?.voucher_entries_id !== "0"
            ? item.voucher_entries_id
            : item?.id;

      if (!deleteId) {
        toast.error("No valid voucher ID found for deletion");
        setLoading(false);
        return;
      }
      const formData = new FormData();
      formData.append("uid", deleteId);
      formData.append("id", deleteId);
      formData.append("company_id", userData?.company_id);

      const deletePromise = dispatch(voucherEntryDelete(formData));
      promises.push(deletePromise);
    }

    Promise.all(promises)
      .then((responses) => {
        responses.forEach((response, index) => {
          if (response.status) {
            deleteMessages.success.push(
              response?.message || "Deleted successfully!",
            );
          } else {
            deleteMessages.failure.push(response?.message || "Fail!");
          }
        });
        if (deleteMessages.success.length > 0) {
          setSelectedItems([]);
          toast.success(deleteMessages.success.join("\n"));
          fetchData(); // Refresh the list
        }
        if (deleteMessages.failure.length > 0) {
          toast.warn(deleteMessages.failure.join("\n"));
        }
        setIsDeleteModalOpen(false);
        setLoading(false);
      })
      .catch((error) => {
        toast.error(error?.message || "Failed!");
        setIsDeleteModalOpen(false);
        setLoading(false);
      });
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isDeleteModalOpen) {
        if (e.key === "a" && e.ctrlKey) {
          e.preventDefault();
          yPressToDelete();
        }
        if (e.key === "y" || e.key === "Y") {
          e.preventDefault();
          yPressToDelete();
        }
        if (e.key === "n" || e.key === "N") {
          e.preventDefault();
          setIsDeleteModalOpen(false);
        }
      } else if (
        !Object.keys(modal_state || {}).some((key) => modal_state[key])
      ) {
        if (e.key === "Enter" && e.ctrlKey) {
          e.preventDefault();
          const item = displayData[selectedItemIndex];
          if (item && !item.is_godown_row) {
            navigate(
              `../voucher-transaction-alter/${item?.voucher_entries_id > 0 ? item?.voucher_entries_id : item?.uid}`,
            );
          }
        }
        if (e.key === "F12") {
          e.preventDefault();
          dispatch(openModal("configure"));
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    isDeleteModalOpen,
    selectedItems,
    selectedItemIndex,
    displayData,
    userData,
    modal_state,
  ]);

  const handleDoubleClick = (index) => {
    const item = displayData[index];
    if (item && !item.is_godown_row && !item.is_location_header) {
      navigate(
        `../voucher-transaction-view/${item?.voucher_entries_id > 0 ? item?.voucher_entries_id : item?.uid}`,
      );
    }
  };

  return (
    <div className="section">
      <div className="detail">
        {isDeleteModalOpen ? (
          <div style={{ zIndex: 10000 }}>
            <DeletConfirm
              handleSubmit={yPressToDelete}
              loading={loading}
              closeModal={() => setIsDeleteModalOpen(false)}
            />
          </div>
        ) : null}
        {modal_state?.period ? (
          <Period setDate={setDate} date={date} setLoading={setLoading} />
        ) : null}
        {modal_state?.configure ? (
          <Configure
            state_configure={state_configure}
            setState_configure={setState_configure}
            setLoading={setLoading}
          />
        ) : null}
        {modal_state?.stockItem ? (
          <StockItemFilter
            setDate={setDate}
            date={date}
            setLoading={setLoading}
            setStockItem={() => dispatch(closeModal("stockItem"))}
          />
        ) : null}
        {modal_state?.changeView ? (
          <ChangeView
            setDate={setDate}
            date={date}
            setChangeView={() => dispatch(closeModal("changeView"))}
          />
        ) : null}
        {modal_state?.godownWise ? (
          <GodownFilter
            date={date}
            setDate={setDate}
            setGodownWise={() => dispatch(closeModal("godownWise"))}
          />
        ) : null}
        <div className="container-fluid">
          <NavLayout>
            Stock Item Vouchers : {data?.stock_item_name} :{" "}
            {date?.location_id
              ? `(Godown : ${date?.location_name})`
              : "(Godown : Primary)"}
            <span>{`${dateFormateShow(date?.send_from_date) === "NaN"
                ? null
                : dateFormateShow(date?.send_from_date)
              } To ${dateFormateShow(date?.send_to_date) === "NaN"
                ? null
                : dateFormateShow(date?.send_to_date)
              }`}</span>
          </NavLayout>
        </div>
        <TableLayoutItem
          total_inwards_quantity={totals.total_inwards_quantity}
          total_outwards_quantity={totals.total_outwards_quantity}
          total_closing_quantity={totals.total_closing_quantity}
          total_inwards_value={totals.total_inwards_value}
          total_outwards_value={totals.total_outwards_value}
          total_closing_value={totals.total_closing_value}
          unit_name={unitName}
          data={data}
          ref={boxRef}
        >
          <div style={{ width: "100%" }} className={`custom_row_item`}>
            <div
              style={{ width: "7%" }}
              className="col-2 m-0 p-0 pe-1 text-end table_body_font"
            >
              {dateFormateShow(data?.opening_date)}
            </div>
            <div
              style={{ width: "24%" }}
              className="col-6 m-0 p-0 ps-1 table_body_font text-start"
            >
              {" "}
              Opening Balance
            </div>
            <div
              style={{ width: "10%" }}
              className="col-2 m-0 p-0 text-start table_body_font"
            ></div>
            <div
              style={{ width: "8%" }}
              className="col-2 m-0 p-0 text-end table_body_font"
            ></div>

            <div style={{ width: "17%" }} className="col-4  m-0 p-0">
              <div className="row m-0 p-0" style={{ width: "100%" }}>
                <div
                  className="col-4 text-end p-0 m-0 table_body_font"
                  style={{ overflow: "hidden", whiteSpace: "nowrap" }}
                >
                  {parseNumeric(data?.opening_data?.quantity) > 0
                    ? data?.opening_data?.quantity
                    : ""}
                </div>
                <div
                  className="col-3 m-0 p-0 text-end table_body_font"
                  style={{ overflow: "hidden" }}
                >
                  {handleZeroValue(data?.opening_data?.rate)}
                </div>
                <div
                  className="col-5 text-end p-0 m-0 pe-1 table_body_font"
                  style={{
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    wordSpacing: -7,
                  }}
                >
                  {parseNumeric(data?.opening_data?.value) > 0
                    ? currencyFormate(parseNumeric(data?.opening_data?.value))
                    : ""}
                </div>
              </div>
            </div>
            <div style={{ width: "17%" }} className="col-4  m-0 p-0">
              <div className="row m-0 p-0" style={{ width: "100%" }}>
                <div className="col-4 text-end p-0 m-0 table_body_font"></div>
                <div className="col-3 m-0 p-0 text-end table_body_font"></div>
                <div className="col-5 text-end p-0 pe-1 m-0 table_body_font"></div>
              </div>
            </div>

            <div style={{ width: "17%" }} className="col-4  m-0 p-0">
              <div className="row m-0 p-0" style={{ width: "100%" }}>
                <div className="col-4 text-end p-0 m-0 table_body_font">
                  {parseNumeric(data?.closing_data?.quantity) > 0
                    ? data?.closing_data?.quantity
                    : ""}
                </div>
                <div className="col-3 text-end m-0 p-0 table_body_font">
                  {handleZeroValue(data?.closing_data?.rate)}
                </div>
                <div className="col-5 text-end p-0 pe-1 m-0 table_body_font">
                  {handleZeroValue(data?.closing_data?.value)}
                </div>
              </div>
            </div>
          </div>
          {loading ? (
            <div
              className="col-12 d-flex align-items-center justify-content-center"
              style={{ height: "70vh" }}
            >
              <Indicator />
            </div>
          ) : (
            displayData.map((item, index) => {
              if (item.is_location_header) {
                return (
                  <div
                    key={index}
                    className={
                      selectedItems.includes(index) ? "selected_multi" : ""
                    }
                  >
                    <div
                      style={{ cursor: "pointer", width: "100%" }}
                      className={`custom_row_item ${index === selectedItemIndex ? (selectedItems.includes(index) ? "selected_multi_selected" : "selected") : ""}`}
                      onClick={(e) => {
                        e?.preventDefault();
                        setSelectedItemIndex(index);
                      }}
                    >
                      <div
                        style={{
                          width: "49%",
                          fontStyle: "italic",
                          color: "var(--theme-color)",
                        }}
                        className="m-0 p-0 ps-1 table_body_font text-start fw-bold"
                      >
                        {item.location_name}
                      </div>

                      <div
                        style={{ width: "17%", color: "var(--theme-color)" }}
                        className="col-4 m-0 p-0 fw-bold"
                      >
                        <div className="row m-0 p-0" style={{ width: "100%" }}>
                          <div className="col-4 text-end p-0 m-0 table_body_font">
                            {item.total_iw_qty
                              ? `${item.total_iw_qty} ${unitName}`.trim()
                              : ""}
                          </div>
                          <div className="col-3 m-0 p-0 text-end table_body_font">
                            {handleZeroValue(item.total_iw_rate)}
                          </div>
                          <div
                            className="col-5 text-end p-0 pe-1 m-0 table_body_font"
                            style={{
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              wordSpacing: -7,
                            }}
                          >
                            {handleZeroValue(item.total_iw_value)}
                          </div>
                        </div>
                      </div>

                      <div
                        style={{ width: "17%", color: "var(--theme-color)" }}
                        className="col-4 m-0 p-0 fw-bold"
                      >
                        <div className="row m-0 p-0" style={{ width: "100%" }}>
                          <div className="col-4 text-end p-0 m-0 table_body_font">
                            {item.total_ow_qty
                              ? `${item.total_ow_qty} ${unitName}`.trim()
                              : ""}
                          </div>
                          <div className="col-3 m-0 p-0 text-end table_body_font">
                            {handleZeroValue(item.total_ow_rate)}
                          </div>
                          <div className="col-5 text-end p-0 pe-1 m-0 table_body_font">
                            {handleZeroValue(item.total_ow_value)}
                          </div>
                        </div>
                      </div>

                      <div
                        style={{ width: "17%", color: "var(--theme-color)" }}
                        className="col-4 m-0 p-0 fw-bold"
                      >
                        <div className="row m-0 p-0" style={{ width: "100%" }}>
                          <div className="col-4 text-end p-0 m-0 table_body_font">
                            {item.closing_qty
                              ? `${item.closing_qty} ${unitName}`.trim()
                              : ""}
                          </div>
                          <div className="col-3 text-end m-0 p-0 table_body_font">
                            {handleZeroValue(item.closing_rate)}
                          </div>
                          <div className="col-5 text-end p-0 pe-1 m-0 table_body_font">
                            {handleZeroValue(item.closing_val)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }

              const {
                inwardQty: item_iw_qty,
                inwardValue: item_iw_value,
                inwardRate: item_iw_rate,
                outwardQty: item_Ow_qty,
                outwardValue: item_Ow_value,
                outwardRate: item_Ow_rate,
                inwardQtyText,
                inwardValueText,
                outwardQtyText,
                outwardValueText,
              } = getEffectiveVoucherFields(item);

              let item_iw_display_value = item_iw_value;
              if (
                item_iw_display_value === 0 &&
                item_iw_qty > 0 &&
                item_iw_rate > 0
              ) {
                item_iw_display_value = item_iw_qty * item_iw_rate;
              }

              let is_debit_note =
                item?.voucher_type === "Debit Note" ||
                item?.voucher_type_name === "Debit Note" ||
                item?.inwardData?.voucher_type === "Debit Note";
              let is_credit_note =
                item?.voucher_type === "Credit Note" ||
                item?.voucher_type_name === "Credit Note" ||
                item?.outwardData?.voucher_type === "Credit Note";

              if (is_credit_note) {
                cumulativeQty += item_Ow_qty;
                cumulativeValue +=
                  item_Ow_value > 0
                    ? item_Ow_value
                    : item_Ow_qty * cumulativeRate;
              } else if (is_debit_note) {
                cumulativeQty -= item_iw_qty;
                cumulativeValue -=
                  item_iw_value > 0
                    ? item_iw_value
                    : item_iw_qty * cumulativeRate;
              } else {
                if (item_iw_qty > 0) {
                  cumulativeQty += item_iw_qty;
                  cumulativeValue += item_iw_value;
                }
                if (item_Ow_qty > 0) {
                  cumulativeQty -= item_Ow_qty;
                  cumulativeValue -= item_Ow_qty * cumulativeRate;
                }
              }

              if (cumulativeQty > 0) {
                cumulativeRate = cumulativeValue / cumulativeQty;
                lastKnownRate = cumulativeRate;
              } else {
                cumulativeRate = lastKnownRate;
              }

              let closing_quantity = cumulativeQty;
              let closing_rate_for_row = cumulativeRate;
              let closing_value = cumulativeValue;
              return (
                <div
                  key={index}
                  className={
                    selectedItems.includes(index) ? "selected_multi" : ""
                  }
                >
                  <div
                    style={{ cursor: "pointer", width: "100%" }}
                    className={`custom_row_item ${index === selectedItemIndex
                        ? selectedItems.includes(index)
                          ? "selected_multi_selected"
                          : "selected"
                        : ""
                      }`}
                    onClick={(e) => {
                      e?.preventDefault();
                      setSelectedItemIndex(index);
                    }}
                    onDoubleClick={(e) => {
                      e.preventDefault();
                      handleDoubleClick(index);
                    }}
                  >
                    <div
                      style={{ width: "7%" }}
                      className="col-2 m-0 p-0 pe-1 text-end table_body_font"
                    >
                      {dateFormateShow(item?.create_date)}
                    </div>
                    <div
                      style={{ width: "24%" }}
                      className={`col-6 m-0 p-0 ps-1 table_body_font text-start ${item.is_godown_row ? "ps-4" : ""}`}
                    >
                      {" "}
                      {item?.party_ledger_name}{" "}
                      {String(date?.location_id).includes(",") &&
                        item?.location_name &&
                        !item.is_godown_row
                        ? `(${item.location_name})`
                        : ""}
                    </div>
                    <div
                      style={{ width: "10%" }}
                      className="col-2 m-0 p-0 text-start table_body_font"
                    >
                      {item?.voucher_type_name}
                    </div>
                    <div
                      style={{ width: "8%" }}
                      className="col-2 m-0 p-0 pe-1 text-end table_body_font"
                    >
                      {item?.serial_no}
                    </div>

                    <div style={{ width: "17%" }} className="col-4  m-0 p-0">
                      <div className="row m-0 p-0" style={{ width: "100%" }}>
                        <div
                          className="col-4 text-end p-0 m-0 table_body_font"
                          style={{ overflow: "hidden", whiteSpace: "nowrap" }}
                        >
                          {item_iw_qty > 0
                            ? `${is_debit_note ? "(-)" : ""} ${inwardQtyText || item?.inwardData?.quantity}`
                            : ""}
                        </div>
                        <div
                          className="col-3 m-0 p-0 text-end table_body_font"
                          style={{ overflow: "hidden" }}
                        >
                          {item_iw_qty > 0 ? handleZeroValue(item_iw_rate) : ""}
                        </div>
                        <div
                          className="col-5 text-end p-0 m-0 pe-1 table_body_font"
                          style={{
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            wordSpacing: -7,
                          }}
                        >
                          {item_iw_display_value > 0
                            ? `${is_debit_note ? "(-)" : ""} ${handleZeroValue(inwardValueText || item?.inwardData?.value)}`
                            : ""}
                        </div>
                      </div>
                    </div>
                    <div style={{ width: "17%" }} className="col-4  m-0 p-0">
                      <div className="row m-0 p-0" style={{ width: "100%" }}>
                        <div className="col-4 text-end p-0 m-0 table_body_font">
                          {item_Ow_qty > 0
                            ? `${is_credit_note ? "(-)" : ""} ${outwardQtyText || item?.outwardData?.quantity}`
                            : ""}
                        </div>
                        <div className="col-3 m-0 p-0 text-end table_body_font">
                          {item_Ow_qty > 0 ? handleZeroValue(item_Ow_rate) : ""}
                        </div>
                        <div className="col-5 text-end p-0 pe-1 m-0 table_body_font">
                          {item_Ow_value > 0
                            ? `${is_credit_note ? "(-)" : ""} ${handleZeroValue(outwardValueText || item?.outwardData?.value)}`
                            : ""}
                        </div>
                      </div>
                    </div>

                    <div style={{ width: "17%" }} className="col-4  m-0 p-0">
                      <div className="row m-0 p-0" style={{ width: "100%" }}>
                        <div className="col-4 text-end p-0 m-0 table_body_font">
                          {closing_quantity
                            ? `${closing_quantity} ${unitName}`.trim()
                            : ""}
                        </div>
                        <div className="col-3 text-end m-0 p-0 table_body_font">
                          {handleZeroValue(closing_rate_for_row)}
                        </div>
                        <div className="col-5 text-end p-0 pe-1 m-0 table_body_font">
                          {handleZeroValue(closing_value)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </TableLayoutItem>
        <StockItemVoucherFooter
          spaceFunction={() => toggleSelection(selectedItemIndex)}
          removeLineFunction={removeSelectedItems}
          restoreLineFunction={restoreSelectedItems}
          setShow={() => navigate(-1)}
          setStockGroup={() => {
            const item = displayData[selectedItemIndex];
            if (item && !item.is_godown_row && !item.is_location_header) {
              navigate(
                `../voucher-transaction-alter/${item?.voucher_entries_id > 0 ? item?.voucher_entries_id : item?.uid}`,
              );
            }
          }}
          setStockCate={() => {
            const item = displayData[selectedItemIndex];
            if (item && !item.is_godown_row && !item.is_location_header) {
              navigate(`../voucherstransaction`, {
                state: {
                  create_date: dateFormateShow(item?.create_date),
                  voucher_type_id: item?.voucher_type_id,
                  voucher_type_name: item?.voucher_type_name,
                },
              });
            } else {
              navigate(`../voucherstransaction`);
            }
          }}
          setStockCompany={() => {
            const item = displayData[selectedItemIndex];
            if (item && !item.is_godown_row && !item.is_location_header) {
              navigate(
                `../voucher-transaction/${item?.voucher_entries_id > 0 ? item?.voucher_entries_id : item?.uid}`,
              );
            }
          }}
          setGodownWise={() => {
            navigate(`../voucherstransaction`);
          }}
          deleteFunction={() => {
            setIsDeleteModalOpen(true);
          }}
        />
      </div>
      <div className="help border_1_1px_left backgroud_color_1 position-relative">
        {modal_state?.period ||
          modal_state?.stockItem ||
          modal_state?.changeView ||
          modal_state?.godownWise ? null : (
          <StockItemVoucherHelp
            items_formate_of_report={
              String(date?.location_id).includes(",") ? "Detailed" : "Condensed"
            }
            godowns_formate_of_report={godowns_formate_of_report}
            setShow={() => dispatch(openModal("period"))}
            setStockItem={() => dispatch(openModal("stockItem"))}
            setChangeView={() => dispatch(openModal("changeView"))}
          />
        )}
      </div>
    </div>
  );
};

export default StockItemVoucher;
